// src/components/home/PackCoverCard.jsx
//
// A study-pack card for the marketing home page (home-4a-hero): the 2c cover palette
// on top, the existing card body underneath.
//
// Compact display of the existing public catalogue; statuses and purchase gates
// stay admin-owned. Full editorial descriptions remain available in Details.
import React from 'react';
import ReferenceCurrency from '../ReferenceCurrency.jsx';
import { getPackTheme } from '../../utils/packBadge.js';
import { findParentBundle } from '../../utils/packHelpers.js';
import { packCardOffer } from './packCardCopy.js';

export default function PackCoverCard({ pack, packs, signedIn, onNavigate }) {
    // 2c: cover colour and its auto-picked ink, from the same helper the library and
    // the reader use -- so a pack looks the same everywhere it appears.
    const theme = getPackTheme(pack);
    const available = pack.status === 'available';

    const offer = packCardOffer(pack, packs, signedIn);
    const parent = findParentBundle(packs, pack.id);
    const goView = () => onNavigate(offer.href);

    return (
        <div className="rounded-[var(--radius-card)] border border-book-line bg-book-page shadow-[var(--shadow-card)] overflow-hidden flex flex-col">
            {/* Cover block */}
            <div className="p-5 min-h-[112px] flex flex-col justify-between"
                style={{ background: theme.cover, color: theme.primary }}>
                <div className="flex items-start justify-between gap-2">
                    <div className="btl-exam-code font-display text-xs tracking-wide min-w-0 break-words font-semibold opacity-80">
                        {pack.paper}
                    </div>
                    <div className="flex-shrink-0 whitespace-nowrap text-xs font-display px-2.5 py-1 rounded-full border font-medium"
                        style={{ borderColor: theme.line, opacity: available ? 1 : 0.75 }}>
                        {available ? 'Available' : 'Coming Soon'}
                    </div>
                </div>
                <h3 className="font-display text-lg font-bold leading-tight mt-3 [overflow-wrap:anywhere]">{pack.label}</h3>
            </div>

            {/* Body */}
            <div className="p-5 flex flex-col flex-grow">
                <p className="font-serif text-book-text text-sm leading-relaxed mb-3">{offer.summary}</p>
                {offer.memberNames.length > 0 && (
                    <div className="font-serif text-book-muted text-sm mb-3">
                        <p className="font-display text-xs font-semibold mb-1">Included packs</p>
                        <ul className="list-disc pl-4 space-y-1">{offer.memberNames.map(name => <li key={name}>{name}</li>)}</ul>
                    </div>
                )}
                {pack.description && <details className="font-serif text-sm text-book-muted mb-4">
                    <summary className="cursor-pointer font-display text-book-accent text-xs font-semibold py-2">Full contents &amp; description</summary>
                    <p className="leading-relaxed mt-2 whitespace-pre-line">{pack.description}</p>
                </details>}
                {available && (
                    <div className="mt-auto pt-4 border-t border-book-line">
                        {/* Bundle clarity: a member sold only inside a bundle shows no
                            standalone price (would read as buy-separately) -- it names the
                            bundle instead. View still opens the pack. Ordinary packs
                            (purchasable) render the price exactly as before. */}
                        {pack.purchasable === false ? (
                            <div>
                                <p className="font-serif text-book-muted text-sm leading-snug mb-3">
                                    {parent ? `Included in ${parent.label}. Not sold separately.` : 'Not sold separately.'}
                                </p>
                                <button onClick={goView}
                                    className="w-full px-4 py-2.5 rounded-full border border-book-accent text-book-accent font-display text-xs hover:bg-book-accent hover:text-book-pageDark transition-colors font-semibold">
                                    {offer.action}
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="font-display text-book-text text-2xl font-bold">{offer.price || 'Price shown at checkout'}</div>
                                {pack.price > 0 && <ReferenceCurrency amount={pack.price} compact />}
                                <p className="font-serif text-book-muted text-xs mt-1 mb-3">12 months from unlock · No automatic renewal</p>
                                <div>
                                    {/* Signed OUT + free items -> the PUBLIC free preview (feel the
                                        content before signing up); no free items -> /login as before.
                                        Signed IN -> the library, unchanged. */}
                                    <button onClick={goView}
                                        className="w-full px-4 py-2.5 rounded-full border border-book-accent text-book-accent font-display text-xs hover:bg-book-accent hover:text-book-pageDark transition-colors font-semibold">
                                        {offer.action}
                                    </button>
                                </div>
                            </>
                        )}
                        {!signedIn && offer.free && <p className="font-serif text-book-muted text-xs mt-2 text-center">No account or payment needed for the sample.</p>}
                    </div>
                )}
            </div>
        </div>
    );
}
